import { useState } from "react";
import { StreamsClient, type StreamSummary, type SubjectSummary } from "../api/generated";
import { usePermissions } from "../auth/SessionContext";
import { Alert, Chip, Empty, Field, Panel, describeError, useAsync } from "../components/ui";

const streams = new StreamsClient("");

const POLICIES = [
  {
    value: "none",
    label: "none — human notices",
    hint: "No payload validation.",
  },
  {
    value: "optional",
    label: "optional — validate if present",
    hint: "A payload is optional, and validated when present.",
  },
  {
    value: "strict",
    label: "strict — payload required and validated",
    hint: "A payload is required, and must match the schema. Use this for subjects other systems consume.",
  },
] as const;

const EXAMPLE_SCHEMA = `{
  "type": "object",
  "required": ["booking_id", "new_deadline"],
  "properties": {
    "booking_id": { "type": "integer" },
    "new_deadline": { "type": "string", "format": "date-time" }
  }
}`;

/**
 * Console module: <b>Streams &amp; subjects</b>.
 *
 * Until a stream exists nothing can be published at all, so this is the first screen a new client
 * company needs. A stream is a named space with its own retention and permissions; a subject is
 * what a message inside it is about, and the subject carries the schema policy (§8.7).
 */
export function StreamsPage() {
  const { hasCapability } = usePermissions();
  const { data, error, loading, reload } = useAsync(() => streams.streams_List());
  const [editing, setEditing] = useState<StreamSummary | null>(null);
  const [subjectFor, setSubjectFor] = useState<{ stream: StreamSummary; subject: SubjectSummary | null } | null>(null);

  const canAdminStreams = hasCapability("streams:admin");
  const canAdminSubjects = hasCapability("subjects:admin");

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Streams &amp; subjects</h1>
          <p className="sub">
            A stream is a named space with its own retention and permissions. A subject names what a
            message is about and sets whether its payload is validated. At least one stream must
            exist before anything can be published.
          </p>
        </div>
      </div>

      {error && <Alert kind="error">{error}</Alert>}

      {canAdminStreams && (
        <StreamEditor
          key={editing?.key ?? "new"}
          stream={editing}
          onDone={() => { setEditing(null); void reload(); }}
          onCancel={editing ? () => setEditing(null) : undefined}
        />
      )}

      {loading ? (
        <Panel><Empty>Loading…</Empty></Panel>
      ) : (data ?? []).length === 0 ? (
        <Panel title="Streams">
          <Empty>
            No streams yet. Create one above, then register the subjects your systems will publish
            under it.
          </Empty>
        </Panel>
      ) : (
        data!.map((stream) => (
          <Panel
            key={stream.id}
            title={`${stream.name} · ${stream.key}`}
            actions={
              <div style={{ display: "flex", gap: 8 }}>
                {canAdminSubjects && (
                  <button
                    className="ghost small"
                    onClick={() => setSubjectFor({ stream, subject: null })}
                  >
                    Add subject
                  </button>
                )}
                {canAdminStreams && (
                  <button className="ghost small" onClick={() => setEditing(stream)}>Edit stream</button>
                )}
              </div>
            }
          >
            <div className="row" style={{ marginBottom: 12, gap: 18 }}>
              <div>
                <div className="muted" style={{ fontSize: 11.5 }}>OWNER SCOPE</div>
                <div>{stream.ownerScope}</div>
              </div>
              <div>
                <div className="muted" style={{ fontSize: 11.5 }}>LAST SEQUENCE</div>
                <div className="mono">{stream.lastSequence}</div>
              </div>
              <div>
                <div className="muted" style={{ fontSize: 11.5 }}>RETENTION</div>
                <div>{stream.retentionDays ? `${stream.retentionDays} days` : "not set"}</div>
              </div>
              <div style={{ flex: 1 }}>
                <div className="muted" style={{ fontSize: 11.5 }}>DESCRIPTION</div>
                <div>{stream.description || <span className="muted">—</span>}</div>
              </div>
            </div>

            {(stream.subjects ?? []).length === 0 ? (
              <Empty>
                No subjects registered. Publishing still works without one — register a subject to
                attach a schema policy to it.
              </Empty>
            ) : (
              <table>
                <thead>
                  <tr><th>Subject</th><th>Policy</th><th>Schema</th><th>Description</th><th></th></tr>
                </thead>
                <tbody>
                  {stream.subjects!.map((s) => (
                    <tr key={s.id}>
                      <td className="mono">{s.subject}</td>
                      <td><PolicyChip policy={s.policy} /></td>
                      <td className="muted">
                        {s.jsonSchema ? `${s.jsonSchema.length} characters` : "—"}
                      </td>
                      <td className="muted">{s.description || "—"}</td>
                      <td>
                        {canAdminSubjects && (
                          <button
                            className="ghost small"
                            onClick={() => setSubjectFor({ stream, subject: s })}
                          >
                            Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
        ))
      )}

      {subjectFor && (
        <SubjectEditor
          stream={subjectFor.stream}
          subject={subjectFor.subject}
          onDone={() => { setSubjectFor(null); void reload(); }}
          onCancel={() => setSubjectFor(null)}
        />
      )}
    </>
  );
}

function PolicyChip({ policy }: { policy?: string }) {
  if (policy === "strict") return <Chip kind="ok">strict</Chip>;
  if (policy === "optional") return <Chip kind="warn">optional</Chip>;
  return <Chip>none</Chip>;
}

function StreamEditor({ stream, onDone, onCancel }: {
  stream: StreamSummary | null;
  onDone: () => void;
  onCancel?: () => void;
}) {
  const [key, setKey] = useState(stream?.key ?? "");
  const [name, setName] = useState(stream?.name ?? "");
  const [description, setDescription] = useState(stream?.description ?? "");
  const [retention, setRetention] = useState(stream?.retentionDays?.toString() ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await streams.streams_Upsert(key, {
        key,
        name,
        description: description || undefined,
        retentionDays: retention ? Number(retention) : undefined,
      });
      onDone();
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel
      title={stream ? `Edit stream — ${stream.key}` : "Add a stream"}
      actions={onCancel && <button className="ghost small" onClick={onCancel}>Cancel</button>}
    >
      {error && <Alert kind="error">{error}</Alert>}
      <div className="row">
        <Field label="Key">
          <input
            value={key}
            onChange={(e) => setKey(e.target.value)}
            disabled={!!stream}
            placeholder="operations"
          />
        </Field>
        <Field label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Operations" />
        </Field>
        <Field label="Retention (days, optional)">
          <input
            value={retention}
            onChange={(e) => setRetention(e.target.value.replace(/[^0-9]/g, ""))}
            placeholder="—"
          />
        </Field>
        <button onClick={submit} disabled={busy || !key || !name}>
          {stream ? "Save" : "Create"}
        </button>
      </div>
      <Field label="Description">
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Deadline changes, stock notices, anything operational"
        />
      </Field>
      <p className="muted" style={{ fontSize: 12, margin: 0 }}>
        The key is lowercase letters, digits, underscore or hyphen, and cannot be changed once
        saved. Retention is recorded but not yet enforced.
      </p>
    </Panel>
  );
}

/**
 * The JSON Schema editor. Validity is checked here before submitting so a typo is caught by the
 * person who made it — but the API parses the document too, and it is the API that decides.
 */
function SubjectEditor({ stream, subject, onDone, onCancel }: {
  stream: StreamSummary;
  subject: SubjectSummary | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(subject?.subject ?? "");
  const [policy, setPolicy] = useState(subject?.policy ?? "none");
  const [schema, setSchema] = useState(subject?.jsonSchema ?? "");
  const [description, setDescription] = useState(subject?.description ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const schemaProblem = describeSchemaProblem(schema);
  const needsSchema = policy === "strict" && !schema.trim();
  const ready = !!name && !schemaProblem && !needsSchema;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await streams.streams_UpsertSubject(stream.key!, name, {
        subject: name,
        policy,
        jsonSchema: schema.trim() || undefined,
        description: description || undefined,
      });
      onDone();
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel
      title={subject ? `Edit subject — ${subject.subject}` : `Register a subject on ${stream.key}`}
      actions={<button className="ghost small" onClick={onCancel}>Cancel</button>}
    >
      {error && <Alert kind="error">{error}</Alert>}

      <div className="row">
        <Field label="Subject">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!!subject}
            placeholder="bookings.deadline.extended"
          />
        </Field>
        <Field label="Schema policy">
          <select value={policy} onChange={(e) => setPolicy(e.target.value)}>
            {POLICIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </Field>
      </div>

      <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
        {POLICIES.find((p) => p.value === policy)?.hint}
      </p>

      <Field label="Description">
        <input value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>

      {policy !== "none" && (
        <Field label="JSON Schema">
          <textarea
            className="mono"
            style={{ minHeight: 220 }}
            value={schema}
            onChange={(e) => setSchema(e.target.value)}
            placeholder={EXAMPLE_SCHEMA}
            spellCheck={false}
          />
        </Field>
      )}

      {policy !== "none" && schemaProblem && <Alert kind="error">{schemaProblem}</Alert>}
      {policy !== "none" && !schemaProblem && schema.trim() && (
        <Alert kind="ok">Valid JSON.</Alert>
      )}
      {needsSchema && (
        <Alert kind="warn">A strict subject requires a schema.</Alert>
      )}

      <div className="row" style={{ marginTop: 4 }}>
        <button onClick={submit} disabled={busy || !ready}>
          {subject ? "Save subject" : "Register subject"}
        </button>
        {policy !== "none" && !schema.trim() && (
          <button className="ghost" onClick={() => setSchema(EXAMPLE_SCHEMA)}>Start from an example</button>
        )}
      </div>

      <p className="muted" style={{ fontSize: 12, marginTop: 10, marginBottom: 0 }}>
        A subject is dot-separated tokens with no wildcards — <span className="mono">orders.*</span>{" "}
        is a grant pattern, not a subject.
      </p>
    </Panel>
  );
}

/** Parse-level checking only. Whether it is a usable *schema* is the API's answer, not ours. */
function describeSchemaProblem(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch (e) {
    return `Not valid JSON — ${(e as Error).message}`;
  }

  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return "A JSON Schema document is an object, not an array or a bare value.";
  }

  const keys = Object.keys(parsed as Record<string, unknown>);
  if (keys.length === 0) {
    return "An empty object is a schema that accepts anything, which is the same as policy 'none'.";
  }

  const structural = ["type", "properties", "$ref", "allOf", "anyOf", "oneOf", "enum", "const", "items"];
  if (!keys.some((k) => structural.includes(k))) {
    return `Nothing here constrains anything — expected one of ${structural.slice(0, 4).join(", ")}. As written this accepts any payload.`;
  }

  return null;
}
